import React from 'react';
import { useSWRConfig } from 'swr';
import { useDealEstimations } from '~/hooks/deals/useDealEstimations';
import Styles from './statuses.module.scss';
import { dealCacheKey } from '~/hooks/useDeals';
import { PATH as INSPECTION_REPORT_PATH } from '~/pages/deals/[id]/inspection_reports';
import { PATH as COMPLETE_REPORT_PATH } from '~/pages/deals/[id]/completion_reports';
import { matchDealType } from '~/models/Deal';
import { ReportImageNavigation } from '~/components/common/reports/navigation/ReportImageNavigation';
import { useDealInspectionReportImages } from '~/hooks/deals/useDealInspectionReportImages';
import { useDealCompletionReportImages } from '~/hooks/deals/useDealCompletionReportImages';
import { navigationTitle } from '~/models/DealReportImage';
import { OwnStatusHandleButtons } from '~/components/common/statuses/OwnStatusHandleButtons';
import {
  OWN_IN_ESTIMATION,
  OWN_SEND_ESTIMATE,
  ownStatusMovingValidation
} from '~/models/Status';
import useItandiAccount from '~/hooks/useItandiAccount';
import { ScheduleInputNavigation as VisitScheduleInputNavigation } from '~/components/common/visit_schedule/ScheduleInputNavigation';
import { useDealEstimationHistories } from '~/hooks/deals/useDealEstimationHistories';
import { ConstructionClientNavigation } from '~/components/common/constructions/navigation/ConstructionClientNavigation';
import { UploadFileNavigation } from '~/components/common/upload_files/UploadFileNavigation';
import { create as uploadMemorandumFile } from '~/utils/api/deals/DealMemorandumFile';
import type { NskResponse } from '~/models/Api';
import {
  destroy as destroyUploadFile,
  getFilePath as getUploadedFilePath
} from '~/utils/api/deals/DealUploadedFile';
import type { DealUploadFile } from '~/models/DealUploadFile';
import { useDealContext } from '~/context/DealContext';

export const OwnInEstimation: React.FC = () => {
  const deal = useDealContext();
  const { mutate: globalMutate } = useSWRConfig();
  const { data: account } = useItandiAccount().currentAccount();
  const { data: estimationContext } = useDealEstimations().index(deal.id);
  const { data: dealEstimationHistoryContext } =
    useDealEstimationHistories().index(deal.id);
  const { data: inspectionImages } = useDealInspectionReportImages().index(
    deal.id
  );
  const { data: completeImages } = useDealCompletionReportImages().index(
    deal.id
  );
  const statusDisabled = React.useMemo(() => {
    if (account === null) return true;
    return !ownStatusMovingValidation[OWN_IN_ESTIMATION](account);
  }, [account]);
  if (
    inspectionImages === null ||
    completeImages === null ||
    estimationContext === null ||
    dealEstimationHistoryContext === null
  ) {
    return null;
  }
  const handleMutateDeal = async (): Promise<void> => {
    await globalMutate(dealCacheKey(deal.id));
  };
  const handleUploadMemorandum = async (
    file: File
  ): Promise<NskResponse<void>> => uploadMemorandumFile(file, deal.id);
  const handleDestroyMemorandum = async (
    file: DealUploadFile
  ): Promise<NskResponse<void>> => destroyUploadFile(deal.id, file.id);
  const handleGetMemorandumFilePath = (file: DealUploadFile): string =>
    getUploadedFilePath(deal.id, file);
  return (
    <div>
      <h3 className={Styles.Title}>
        {matchDealType(deal, 'restoration') && '退去手続きの'}見積り作成中です
      </h3>
      <p className={Styles.Description}>
        立会が完了して、見積り作成後にステータスを進めてください
      </p>
      <VisitScheduleInputNavigation
        deal={deal}
        handleMutate={handleMutateDeal}
      />
      <ConstructionClientNavigation title="見積りを入力してください" />
      <div>
        <ReportImageNavigation
          detailPageButtonLabel="画像登録画面"
          detailPagePath={INSPECTION_REPORT_PATH(deal.id)}
          optionalLabel
          reportImages={inspectionImages}
          title={navigationTitle(deal)}
        />
        <ReportImageNavigation
          detailPageButtonLabel="画像登録画面"
          detailPagePath={COMPLETE_REPORT_PATH(deal.id)}
          optionalLabel
          reportImages={completeImages}
          title="完了報告画像を登録してください"
        />
        <UploadFileNavigation
          deal={deal}
          files={deal.dealMemorandumFiles}
          fileType="賃借人同意書"
          handleDestroy={handleDestroyMemorandum}
          handleGetFilePath={handleGetMemorandumFilePath}
          handleMutate={handleMutateDeal}
          handleUpload={handleUploadMemorandum}
          title="賃借人同意書を登録してください"
        />
        <OwnStatusHandleButtons
          backwardDisabled={statusDisabled}
          forwardDisabled={statusDisabled}
          nextStatusId={OWN_SEND_ESTIMATE}
        />
      </div>
    </div>
  );
};
